import * as R from 'ramda';
import * as cnt from './constants';
import {
  createInitialMappingDialogState,
  extractTi2AppKeys,
  getHintsForIntegration,
  INITIAL_MAPPING_DIALOG_VALUES,
  normalizeMappingText,
} from '../mapping-utils';

const getErrorMessage = ({ payload, error }) => {
  if (!payload && !error) return 'Unable to update partner details.';
  if (typeof payload === 'string') return payload;
  const source = payload || error || {};
  if (R.path(['json', 'error'], source)) return R.path(['json', 'error'], source);
  if (R.path(['json', 'message'], source)) return R.path(['json', 'message'], source);
  if (R.path(['error'], source)) return R.path(['error'], source);
  if (R.path(['message'], source)) return R.path(['message'], source);
  return 'Unable to update partner details.';
};

const resetMappingDialog = mappingDialog => ({
  ...mappingDialog,
  open: false,
  index: null,
  values: {
    ...INITIAL_MAPPING_DIALOG_VALUES,
  },
  appKeysLoading: false,
  appKeysError: '',
  localError: '',
  savePending: false,
});

const initialState = {
  activities: [],
  activitiesLoading: false,
  activitiesLoaded: false,
  contacts: [],
  contactsLoading: false,
  contactsLoaded: false,
  locations: [],
  locationsLoading: false,
  locationsLoaded: false,
  files: [],
  filesLoading: false,
  filesLoaded: false,
  partnerInfo: {},
  partnerInfoLoading: false,
  partnerInfoLoaded: false,
  partnerDetailsUpdating: false,
  updateError: '',
  mappingDialog: createInitialMappingDialogState(),
};

const reducer = (state = initialState, action = {}) => {
  const {
    type,
    payload,
    error,
  } = action;

  switch (type) {
    case `${cnt.GET_PARTNER_INFO}_STARTED`:
      return {
        ...state,
        partnerInfoLoading: true,
        contactsLoading: true,
        locationsLoading: true,
      };
    case `${cnt.GET_PARTNER_INFO}_ERROR`:
      return {
        ...state,
        partnerInfoLoading: false,
        contactsLoading: false,
        locationsLoading: false,
        error: payload,
      };
    case `${cnt.GET_PARTNER_INFO}_SUCCESS`: {
      const { partner, contacts, tags, locations } = payload;
      return {
        ...state,
        contactsLoading: false,
        contactsLoaded: true,
        locationsLoading: false,
        locationsLoaded: true,
        partnerInfoLoading: false,
        partnerInfoLoaded: true,
        partnerInfo: partner,
        locations,
        tags,
        contacts,
        error: '',
        updateError: '',
      };
    }
    case `${cnt.GET_FILES}_STARTED`:
      return { ...state, filesLoading: true };
    case `${cnt.GET_FILES}_SUCCESS`:
      return {
        ...state,
        files: payload.contracts,
        filesLoaded: true,
        filesLoading: false,
      };
    case `${cnt.GET_FILES}_ERROR`:
      return { ...state, filesLoaded: false, filesLoading: false };
    case `${cnt.GET_ACTIVITY}_STARTED`:
      return { ...state, activitiesLoading: true };
    case `${cnt.GET_ACTIVITY}_SUCCESS`:
      return {
        ...state,
        activitiesLoaded: true,
        activitiesLoading: false,
        activities: payload,
      };
    case `${cnt.GET_ACTIVITY}_ERROR`:
      return { ...state, activitiesLoaded: false, activitiesLoading: false };
    case `${cnt.UPDATE_PARTNER_DETAILS}_STARTED`:
      return {
        ...state,
        partnerDetailsUpdating: true,
        updateError: '',
      };
    case `${cnt.UPDATE_PARTNER_DETAILS}_SUCCESS`:
      return {
        ...state,
        partnerDetailsUpdating: false,
        updateError: '',
        mappingDialog: state.mappingDialog.savePending
          ? resetMappingDialog(state.mappingDialog)
          : state.mappingDialog,
      };
    case `${cnt.UPDATE_PARTNER_DETAILS}_ERROR`:
      return {
        ...state,
        partnerDetailsUpdating: false,
        updateError: getErrorMessage({ payload, error }),
        mappingDialog: state.mappingDialog.savePending
          ? {
            ...state.mappingDialog,
            savePending: false,
          }
          : state.mappingDialog,
      };
    case cnt.OPEN_MAPPING_DIALOG:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          open: true,
          index: typeof payload?.index === 'number' && payload.index > -1
            ? payload.index
            : null,
          values: {
            ...INITIAL_MAPPING_DIALOG_VALUES,
            ...R.pick(Object.keys(INITIAL_MAPPING_DIALOG_VALUES), payload?.mapping || {}),
          },
          localError: '',
          appKeysError: '',
          savePending: false,
        },
      };
    case cnt.CLOSE_MAPPING_DIALOG:
      return {
        ...state,
        mappingDialog: resetMappingDialog(state.mappingDialog),
      };
    case cnt.UPDATE_MAPPING_DIALOG_FIELD: {
      const key = payload?.key;
      if (!key) return state;
      const nextValues = {
        ...state.mappingDialog.values,
        [key]: payload?.value,
      };
      if (key === 'integrationId') {
        const nextHints = getHintsForIntegration(state.mappingDialog.appKeys, payload?.value || '');
        if (nextHints.indexOf(normalizeMappingText(state.mappingDialog.values.hint)) === -1) {
          nextValues.hint = '';
        }
      }
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          values: nextValues,
          localError: '',
        },
      };
    }
    case cnt.SET_MAPPING_DIALOG_ERROR:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          localError: payload || '',
        },
      };
    case cnt.CLEAR_MAPPING_DIALOG_ERROR:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          localError: '',
        },
      };
    case cnt.SET_MAPPING_DIALOG_SAVE_PENDING:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          savePending: Boolean(payload),
        },
      };
    case `${cnt.LOAD_MAPPING_APP_KEYS}_STARTED`:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          appKeys: [],
          appKeysLoading: true,
          appKeysError: '',
        },
      };
    case `${cnt.LOAD_MAPPING_APP_KEYS}_SUCCESS`: {
      const nextAppKeys = extractTi2AppKeys(payload);
      const currentHint = normalizeMappingText(state.mappingDialog.values.hint);
      const currentIntegrationId = state.mappingDialog.values.integrationId;
      const nextHints = getHintsForIntegration(nextAppKeys, currentIntegrationId);
      const nextHint = !currentHint || !currentIntegrationId || nextHints.indexOf(currentHint) > -1
        ? state.mappingDialog.values.hint
        : '';
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          appKeys: nextAppKeys,
          appKeysLoading: false,
          appKeysError: '',
          values: {
            ...state.mappingDialog.values,
            hint: nextHint,
          },
        },
      };
    }
    case `${cnt.LOAD_MAPPING_APP_KEYS}_ERROR`:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          appKeysLoading: false,
          appKeysError: getErrorMessage({ payload, error }),
        },
      };
    case `${cnt.LOAD_MAPPING_LOOKUP_PARTNERS}_STARTED`:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          lookupPartnersLoading: true,
        },
      };
    case `${cnt.LOAD_MAPPING_LOOKUP_PARTNERS}_SUCCESS`:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          lookupPartnersLoading: false,
          lookupPartners: Array.isArray(payload?.partners) ? payload.partners : [],
        },
      };
    case `${cnt.LOAD_MAPPING_LOOKUP_PARTNERS}_ERROR`:
      return {
        ...state,
        mappingDialog: {
          ...state.mappingDialog,
          lookupPartnersLoading: false,
          lookupPartners: [],
        },
      };
    case cnt.CLEAR_STORE:
      return initialState;
    default:
      return state;
  }
};

export default reducer;
